import i18n from '@/i18n';
import type { Alert } from '@/@types/alert';
import type { Route, Stop } from '@/@types/gtfs-ressource';

interface NetworkAlert {
  alert: Alert;
  /** Names of the alert's known stops — the context of a stop-only alert. */
  stopNames: Array<string>;
}

export interface AlertGroups {
  /** Alerts not tied to any known route: network-wide, stop-only, or unknown entities. */
  network: Array<NetworkAlert>;
  routes: { [routeId: string]: Array<Alert> };
}

/** GTFS effects carrying no label worth showing: 7 (unknown effect) and 8 (other effect). */
const UNLABELLED_EFFECTS = [7, 8];

const UNKNOWN_CAUSE = 1;

/**
 * Alerts deduplicated by `_id` — the store holds one entry per active window,
 * so an alert whose windows overlap is present several times.
 */
export function dedupeAlerts(alerts: Array<Alert>): Array<Alert> {
  const seenIds = new Set<string>();
  return alerts.filter(alert => {
    if (seenIds.has(alert._id)) return false;
    seenIds.add(alert._id);
    return true;
  });
}

/**
 * Alerts informing any of `stopIds`: an alert set on one platform of a stop, or
 * on the station itself, belongs to the whole stop.
 */
export function stopAlerts(alerts: Array<Alert>, stopIds: Array<string>): Array<Alert> {
  return dedupeAlerts(
    alerts.filter(alert =>
      alert.informed_entity?.some(entity => entity.stop_id && stopIds.includes(entity.stop_id)),
    ),
  );
}

/**
 * Alerts newest first: what has just been announced is what the passenger needs.
 * An alert whose publication date cannot be read sorts last rather than jumping
 * to the top — `alertPublishedAt` only fails on an id that is not an ObjectId.
 */
export function sortAlertsByPublication(alerts: Array<Alert>): Array<Alert> {
  return [...alerts].sort(
    (a, b) => (alertPublishedAt(b)?.getTime() ?? 0) - (alertPublishedAt(a)?.getTime() ?? 0),
  );
}

/** Ids of the known routes an alert informs. */
export function alertRouteIds(alert: Alert, routes: { [routeId: string]: Route }): Array<string> {
  const routeIds = new Set<string>();
  alert.informed_entity?.forEach(entity => {
    if (entity.route_id && routes[entity.route_id]) routeIds.add(entity.route_id);
  });
  return [...routeIds];
}

/**
 * Group alerts by informed entity: one list per known route (an alert informing
 * several lines appears in each), and a network group catching everything not
 * tied to a known route — network-wide alerts, stop-only alerts (with their
 * stop names as context), and alerts targeting entities unknown to the current
 * GTFS. An alert must never disappear from the view.
 *
 * Every group comes out newest first: the groups keep the order they are fed.
 */
export function groupAlertsByEntity(
  alerts: Array<Alert>,
  routes: { [routeId: string]: Route },
  stops: { [stopId: string]: Stop },
): AlertGroups {
  const groups: AlertGroups = { network: [], routes: {} };

  sortAlertsByPublication(dedupeAlerts(alerts)).forEach(alert => {
    const routeIds = alertRouteIds(alert, routes);
    if (routeIds.length > 0) {
      routeIds.forEach(routeId => {
        if (!groups.routes[routeId]) groups.routes[routeId] = [];
        groups.routes[routeId].push(alert);
      });
      return;
    }

    const stopNames = (alert.informed_entity ?? [])
      .map(entity => entity.stop_id && stops[entity.stop_id]?.stop_name)
      .filter((name): name is string => Boolean(name));
    groups.network.push({ alert, stopNames });
  });

  return groups;
}

/**
 * Translated label of an alert's effect, or `undefined` when the effect carries none.
 *
 * `i18n.global.t` rather than `useI18n()`: the helper is called from plain computeds as
 * well as from components, and `global.locale` is reactive either way.
 */
export function formatAlertEffect(alert?: Alert): string | undefined {
  const effect = alert?.effect;
  if (!effect || UNLABELLED_EFFECTS.includes(effect)) return undefined;
  return i18n.global.t('alerts.effect.' + effect);
}

/** Translated label of an alert's cause, or `undefined` when absent or `UNKNOWN_CAUSE`. */
export function formatAlertCause(alert?: Alert): string | undefined {
  const cause = alert?.cause;
  if (!cause || cause === UNKNOWN_CAUSE) return undefined;
  const key = 'alerts.cause.' + cause;
  const label = i18n.global.t(key);
  // `t()` answers with the key itself when no message exists: never show that to a passenger.
  return label === key ? undefined : label;
}

/**
 * Publication date of an alert, read from the creation timestamp its MongoDB
 * `_id` carries in its first 4 bytes: the API exposes no `created_at`, and the
 * operator back-office writes the document when the alert is published.
 * `undefined` for an id that is not an ObjectId — nothing beats a wrong date.
 */
export function alertPublishedAt(alert?: Alert): Date | undefined {
  const id = alert?._id;
  if (!id || !/^[0-9a-f]{24}$/.test(id)) return undefined;

  const date = new Date(parseInt(id.slice(0, 8), 16) * 1000);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * How long ago an alert was published, e.g. "Il y a 23h", or `undefined` when
 * its publication date is unknown or still ahead of us (clock skew).
 *
 * The duration is formatted by `Intl` rather than by locale keys of its own:
 * the narrow unit style already spells "23h", "3j" and "12min" in every
 * language the app ships, which is exactly what the mockups show.
 */
export function formatAlertPublishedAgo(alert?: Alert): string | undefined {
  const publishedAt = alertPublishedAt(alert);
  if (!publishedAt) return undefined;

  const elapsedMinutes = (Date.now() - publishedAt.getTime()) / 60_000;
  if (elapsedMinutes < 0) return undefined;

  // An alert published seconds ago reads as one minute old rather than as zero.
  let value = Math.max(1, Math.floor(elapsedMinutes));
  let unit = 'minute';
  if (elapsedMinutes >= 60 * 24) {
    value = Math.floor(elapsedMinutes / (60 * 24));
    unit = 'day';
  } else if (elapsedMinutes >= 60) {
    value = Math.floor(elapsedMinutes / 60);
    unit = 'hour';
  }

  const duration = new Intl.NumberFormat(i18n.global.locale, {
    style: 'unit',
    unit,
    unitDisplay: 'narrow',
  }).format(value);

  return i18n.global.t('alerts.publishedAgo', { duration });
}

/**
 * Redirection link of an alert — the API sends `""` when the operator left it blank. Operators often type
 * the address without its scheme, and anything but http(s) (e.g. `javascript:`) must never become a link.
 */
export function alertUrl(alert?: Alert): string | undefined {
  const raw = alert?.url?.trim();
  if (!raw) return undefined;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}
